export interface PhysicsState {
	position: number; // x position in meters
	velocity: number; // m/s
	acceleration: number; // m/s²
	time: number; // seconds elapsed
}

export interface ForceConfig {
	appliedForce: number; // Newtons
	mass: number; // kg
	frictionCoefficient: number; // μ (0-1)
	gravity: number; // m/s² (default 9.8)
}

export function calculateNetForce(config: ForceConfig): {
	netForce: number;
	frictionForce: number;
	normalForce: number;
	weight: number;
} {
	const weight = config.mass * config.gravity;
	const normalForce = weight;
	const frictionForce =
		config.appliedForce === 0
			? 0
			: Math.min(
					config.frictionCoefficient * normalForce,
					Math.abs(config.appliedForce),
				) * Math.sign(config.appliedForce);

	const netForce = config.appliedForce - frictionForce;

	return { netForce, frictionForce, normalForce, weight };
}

export function calculateAcceleration(
	netForce: number,
	mass: number,
): number {
	if (mass === 0) return 0;
	return netForce / mass; // F = ma => a = F/m
}

export function stepPhysics(
	state: PhysicsState,
	config: ForceConfig,
	dt: number,
): PhysicsState {
	const { netForce } = calculateNetForce(config);
	const acceleration = calculateAcceleration(netForce, config.mass);

	const newVelocity = state.velocity + acceleration * dt;

	// If friction stops the object (velocity crosses zero with no applied force)
	let finalVelocity = newVelocity;
	if (config.appliedForce === 0 && Math.sign(newVelocity) !== Math.sign(state.velocity) && state.velocity !== 0) {
		finalVelocity = 0;
	}

	const newPosition = state.position + finalVelocity * dt;

	return {
		position: newPosition,
		velocity: finalVelocity,
		acceleration,
		time: state.time + dt,
	};
}

export function getInitialState(): PhysicsState {
	return {
		position: 0,
		velocity: 0,
		acceleration: 0,
		time: 0,
	};
}

// Surface friction presets
export const SURFACES: Record<string, { label: string; coefficient: number; color: string }> = {
	ice: { label: "Ice", coefficient: 0.03, color: "#a5f3fc" },
	wood: { label: "Wood", coefficient: 0.3, color: "#d4a574" },
	rubber: { label: "Rubber", coefficient: 0.7, color: "#4a4a4a" },
	concrete: { label: "Concrete", coefficient: 0.6, color: "#9ca3af" },
};
